import { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Stack,
  Typography,
  Alert,
  Switch,
} from '@mui/joy';
import { Download } from '@mui/icons-material';
import { ShiftType } from '@/types/shiftType';
import { User } from '@/types/user';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

interface ExportModalProps {
  open: boolean;
  onClose: () => void;
  shiftTypes: ShiftType[];
  employees?: User[];
  onExportComplete?: () => void;
}

export default function ExportModal({
  open,
  onClose,
  shiftTypes,
  employees,
  onExportComplete,
}: ExportModalProps) {
  const { user } = useAuth();
  const [format, setFormat] = useState<'ics' | 'pdf'>('ics');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [employeeId, setEmployeeId] = useState<number | null>(null);
  const [shiftTypeId, setShiftTypeId] = useState<number | null>(null);
  const [status, setStatus] = useState<'all' | 'draft' | 'published' | 'updated'>('all');
  const [mySchedulesOnly, setMySchedulesOnly] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

  const handleExport = async () => {
    setError(null);
    setIsExporting(true);

    try {
      // Validate date range
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        const daysDiff = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysDiff < 0) {
          setError('End date must be after start date');
          setIsExporting(false);
          return;
        }
        
        if (daysDiff > 365) {
          setError('Date range cannot exceed 365 days');
          setIsExporting(false);
          return;
        }
      }

      const token = localStorage.getItem('accessToken');
      const response = await axios.post(
        `${API_BASE_URL}/schedules/export`,
        {
          format,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          employee_id: employeeId || undefined,
          shift_type_id: shiftTypeId || undefined,
          status: status !== 'all' ? status : undefined,
          my_schedules_only: mySchedulesOnly,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          responseType: 'blob',
        }
      );

      // Create download link
      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.headers['content-disposition']
        ? response.headers['content-disposition'].split('filename=')[1]?.replace(/"/g, '') || 'export.ics'
        : `shiftifi-export-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      if (onExportComplete) {
        onExportComplete();
      }
      onClose();
    } catch (err: any) {
      console.error('Export error:', err);
      setError(err.response?.data?.error || 'Failed to export schedules');
    } finally {
      setIsExporting(false);
    }
  };

  const handleMySchedulesOnlyChange = (checked: boolean) => {
    setMySchedulesOnly(checked);
    if (checked) {
      setEmployeeId(null); // Clear employee filter when "My Schedules Only" is enabled
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 500, width: '100%' }}>
        <ModalClose />
        <Typography level="h4" mb={2}>
          Export Schedules
        </Typography>

        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={2}>
          {/* Format Selection */}
          <FormControl>
            <FormLabel>Format</FormLabel>
            <Select value={format} onChange={(_, value) => setFormat(value || 'ics')}>
              <Option value="ics">iCalendar (.ics)</Option>
              <Option value="pdf" disabled>PDF (Coming Soon)</Option>
            </Select>
          </FormControl>

          {/* Date Range */}
          <FormControl>
            <FormLabel>Date Range (Optional, max 365 days)</FormLabel>
            <Stack spacing={1}>
              <Input
                type="date"
                placeholder="Start date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <Input
                type="date"
                placeholder="End date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                slotProps={{
                  input: {
                    min: startDate || undefined,
                  },
                }}
              />
            </Stack>
          </FormControl>

          {/* My Schedules Only */}
          <FormControl>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <FormLabel>My Schedules Only</FormLabel>
              <Switch
                checked={mySchedulesOnly}
                onChange={(e) => handleMySchedulesOnlyChange(e.target.checked)}
              />
            </Stack>
          </FormControl>

          {/* Employee Filter */}
          {!mySchedulesOnly && employees && employees.length > 0 && (
            <FormControl>
              <FormLabel>Employee</FormLabel>
              <Select
                value={employeeId ?? ''}
                onChange={(_, value) => setEmployeeId(value === '' ? null : value as number)}
                placeholder="All Employees"
              >
                <Option value="">All Employees</Option>
                {employees.map((emp) => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </Option>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Shift Type Filter */}
          <FormControl>
            <FormLabel>Shift Type</FormLabel>
              <Select
              value={shiftTypeId ?? ''}
              onChange={(_, value) => setShiftTypeId(value === '' ? null : value as number)}
              placeholder="All Shift Types"
            >
              <Option value="">All Shift Types</Option>
              {shiftTypes.map((st) => (
                <Option key={st.id} value={st.id}>
                  {st.name}
                </Option>
              ))}
            </Select>
          </FormControl>

          {/* Status Filter */}
          <FormControl>
            <FormLabel>Status</FormLabel>
            <Select value={status} onChange={(_, value) => setStatus(value || 'all')}>
              <Option value="all">All Statuses</Option>
              <Option value="draft">Draft</Option>
              <Option value="published">Published</Option>
              <Option value="updated">Updated</Option>
            </Select>
          </FormControl>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={2}>
            <Button variant="outlined" onClick={onClose} disabled={isExporting}>
              Cancel
            </Button>
            <Button
              startDecorator={<Download />}
              onClick={handleExport}
              loading={isExporting}
            >
              Export
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}

